import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, Users, Target, Loader2, CreditCard, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { partyInfo } from '../mockData';

const Donate = () => {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const presetAmounts = [500, 1000, 2000, 5000];

  const handleAmountSelect = (amount) => {
    setDonationAmount(amount.toString());
    setCustomAmount('');
  };

  const handleCustomAmount = (e) => {
    setCustomAmount(e.target.value);
    setDonationAmount(e.target.value);
  };

  const handleChange = (e) => {
    setDonorInfo({ ...donorInfo, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!donationAmount || parseFloat(donationAmount) <= 0) {
      toast.error(t({ en: 'Please enter a valid donation amount.', bn: 'অনুগ্রহ করে একটি বৈধ দান পরিমাণ লিখুন।' }));
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch('/api/donations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          donor_name: donorInfo.name,
          email: donorInfo.email || undefined,
          phone: donorInfo.phone,
          amount: parseFloat(donationAmount),
          message: donorInfo.message || undefined,
        }),
      });
      if (!response.ok) throw new Error('Failed');
      toast.success(t({
        en: `Your donation of ${donationAmount} BDT has been recorded. Please complete the payment.`,
        bn: `${donationAmount} টাকার আপনার দান রেকর্ড করা হয়েছে। অনুগ্রহ করে পেমেন্ট সম্পন্ন করুন।`,
      }));
      setDonationAmount('');
      setCustomAmount('');
      setDonorInfo({ name: '', email: '', phone: '', message: '' });
    } catch {
      toast.error(t({ en: 'Failed to record donation. Please try again.', bn: 'দান রেকর্ড করতে ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="donate-page">
      {/* Hero Section */}
      <section className="bg-party-green py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t({ en: 'Support Our Cause', bn: 'আমাদের কারণ সমর্থন করুন' })}
          </h1>
          <p className="text-lg text-green-100 max-w-3xl mx-auto">
            {t({
              en: 'Your contribution helps us build a grassroots movement for democratic change.',
              bn: 'আপনার অবদান আমাদের গণতান্ত্রিক পরিবর্তনের জন্য একটি তৃণমূল আন্দোলন গড়তে সাহায্য করে।',
            })}
          </p>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10">
            {t({ en: 'Your Impact', bn: 'আপনার প্রভাব' })}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Users, title: { en: 'Community Organizing', bn: 'সম্প্রদায় সংগঠন' }, description: { en: 'Fund grassroots organizing in all 64 districts', bn: 'সকল ৬৪ জেলায় তৃণমূল সংগঠনে তহবিল' } },
              { icon: Target, title: { en: 'Policy Research', bn: 'নীতি গবেষণা' }, description: { en: 'Support research for evidence-based policies', bn: 'প্রমাণভিত্তিক নীতির জন্য গবেষণা সমর্থন' } },
              { icon: Heart, title: { en: 'Youth Programs', bn: 'যুব কর্মসূচি' }, description: { en: 'Empower young leaders through training', bn: 'প্রশিক্ষণের মাধ্যমে তরুণ নেতাদের ক্ষমতায়ন' } },
              { icon: Landmark, title: { en: 'Transparency', bn: 'স্বচ্ছতা' }, description: { en: 'All donations are publicly disclosed', bn: 'সমস্ত দান প্রকাশ্যে প্রকাশ করা হয়' } },
            ].map((impact, idx) => (
              <Card key={idx} className="text-center hover:shadow-xl transition-shadow duration-300" data-testid={`impact-card-${idx}`}>
                <CardHeader className="pb-2">
                  <div className="mx-auto w-14 h-14 bg-party-green-50 rounded-full flex items-center justify-center mb-3">
                    <impact.icon className="h-7 w-7 text-party-green" />
                  </div>
                  <CardTitle className="text-base">{t(impact.title)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">{t(impact.description)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* QR Code / Payment Info */}
            <Card className="overflow-hidden" data-testid="payment-qr-card">
              <CardHeader className="bg-gradient-to-br from-green-700 to-green-900 text-white">
                <CardTitle className="text-xl flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {t({ en: 'Digital Donation Box', bn: 'ডিজিটাল দান বাক্স' })}
                </CardTitle>
                <p className="text-green-100 text-sm mt-1">
                  {t({ en: 'Scan the QR code with your mobile banking or banking app', bn: 'দান করতে আপনার মোবাইল ব্যাংকিং অ্যাপ অথবা ব্যাংকিং অ্যাপ থেকে স্ক্যান করুন' })}
                </p>
              </CardHeader>
              <CardContent className="p-6 text-center">
                <div className="bg-white border-2 border-gray-100 rounded-xl p-4 inline-block shadow-sm">
                  <img
                    src={partyInfo.bkashQR}
                    alt="Digital Donation QR Code"
                    className="mx-auto w-72 h-auto object-contain"
                    data-testid="bkash-qr-image"
                  />
                </div>
                <div className="mt-6 space-y-3">
                  <div className="bg-party-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">
                      {t({ en: 'Account Name', bn: 'অ্যাকাউন্ট নাম' })}
                    </p>
                    <p className="text-lg font-bold text-party-green">NATIONAL DEMOCRATIC PARTY NDP</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t({ en: 'Phone', bn: 'ফোন' })}</p>
                      <p className="font-semibold text-sm">01732341999</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">{t({ en: 'Branch', bn: 'শাখা' })}</p>
                      <p className="font-semibold text-sm">Nawabganj, Dhaka</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    {t({ en: 'Supports: bKash, Nagad, Rocket, VISA, Mastercard, UnionPay, NPS', bn: 'সমর্থিত: bKash, নগদ, রকেট, VISA, Mastercard, UnionPay, NPS' })}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Donation Form */}
            <Card data-testid="donation-form-card">
              <CardHeader>
                <CardTitle className="text-xl">
                  {t({ en: 'Donation Details', bn: 'দানের বিবরণ' })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="donation-form">
                  <div>
                    <Label>{t({ en: 'Select Amount (BDT)', bn: 'পরিমাণ নির্বাচন করুন (টাকা)' })}</Label>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {presetAmounts.map((amount) => (
                        <Button
                          key={amount}
                          type="button"
                          variant={donationAmount === amount.toString() ? 'default' : 'outline'}
                          onClick={() => handleAmountSelect(amount)}
                          className={donationAmount === amount.toString() ? 'bg-party-red hover:bg-party-red-600' : ''}
                          data-testid={`amount-btn-${amount}`}
                        >
                          {amount}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="customAmount">
                      {t({ en: 'Or Enter Custom Amount', bn: 'অথবা কাস্টম পরিমাণ লিখুন' })}
                    </Label>
                    <Input
                      id="customAmount"
                      type="number"
                      value={customAmount}
                      onChange={handleCustomAmount}
                      placeholder="1000"
                      className="mt-1"
                      data-testid="custom-amount-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="name">{t({ en: 'Your Name', bn: 'আপনার নাম' })} *</Label>
                    <Input id="name" name="name" value={donorInfo.name} onChange={handleChange} required className="mt-1" data-testid="donor-name-input" />
                  </div>

                  <div>
                    <Label htmlFor="email">{t({ en: 'Email', bn: 'ইমেইল' })}</Label>
                    <Input id="email" name="email" type="email" value={donorInfo.email} onChange={handleChange} className="mt-1" data-testid="donor-email-input" />
                  </div>

                  <div>
                    <Label htmlFor="phone">{t({ en: 'Phone Number', bn: 'ফোন নম্বর' })} *</Label>
                    <Input id="phone" name="phone" value={donorInfo.phone} onChange={handleChange} required className="mt-1" data-testid="donor-phone-input" />
                  </div>

                  <div>
                    <Label htmlFor="message">{t({ en: 'Message (Optional)', bn: 'বার্তা (অপশনাল)' })}</Label>
                    <Textarea id="message" name="message" value={donorInfo.message} onChange={handleChange} rows={3} className="mt-1" data-testid="donor-message-input" />
                  </div>

                  <Button type="submit" className="w-full bg-party-red hover:bg-party-red-600" disabled={submitting} data-testid="donation-submit-btn">
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {t({ en: 'Confirm Donation', bn: 'দান নিশ্চিত করুন' })}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
